import { useState } from 'react';
import { IonPage, IonContent, IonButton, IonIcon } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { arrowForwardOutline, chevronBackOutline } from 'ionicons/icons';
import { usePageView } from '../hooks/useLoggerInit';
import { useI18n } from '../i18n/I18nProvider';
import { markOnboardingCompleted } from '../lib/onboarding';

const SLIDE_COUNT = 3;

const OnboardingPage: React.FC = () => {
  const { t } = useI18n();
  const history = useHistory();
  const [index, setIndex] = useState(0);

  usePageView('onboarding');

  const last = index === SLIDE_COUNT - 1;

  const finish = () => {
    markOnboardingCompleted();
    history.replace('/');
  };

  return (
    <IonPage>
      <IonContent fullscreen className="vault-screen onboarding-content">
        <div className="onboarding-shell">
          <div className="onboarding-top">
            <IonButton fill="clear" size="small" className="onboarding-skip" onClick={finish}>
              {t('onboarding.skip')}
            </IonButton>
          </div>

          <div className="onboarding-slide" key={index}>
            {index === 0 && (
              <>
                <h1>{t('onboarding.slide1.title')}</h1>
                <p className="onboarding-body">{t('onboarding.slide1.body')}</p>
              </>
            )}
            {index === 1 && (
              <>
                <div className="onboarding-media">
                  {/* Root-relative asset — works for PWA and Capacitor https scheme */}
                  <img src={`${import.meta.env.BASE_URL}pwa-512x512.png`} alt="" draggable={false} />
                </div>
                <h1>{t('onboarding.slide2.title')}</h1>
                <p className="onboarding-body">{t('onboarding.slide2.body')}</p>
              </>
            )}
            {index === 2 && (
              <>
                <h1>{t('onboarding.slide3.title')}</h1>
                <p className="onboarding-body">{t('onboarding.slide3.body')}</p>
              </>
            )}
          </div>

          <div className="onboarding-footer">
            <div className="onboarding-dots">
              {[0, 1, 2].map((i) => (
                <button
                  key={i}
                  type="button"
                  className={`onboarding-dot${i === index ? ' is-active' : ''}`}
                  onClick={() => setIndex(i)}
                  aria-label={`${i + 1}`}
                />
              ))}
            </div>

            <div className="onboarding-actions">
              {index > 0 ? (
                <IonButton fill="outline" onClick={() => setIndex((i) => i - 1)}>
                  <IonIcon slot="start" icon={chevronBackOutline} />
                  {t('onboarding.back')}
                </IonButton>
              ) : (
                <span />
              )}
              <IonButton
                onClick={() => {
                  if (last) finish();
                  else setIndex((i) => i + 1);
                }}
              >
                {last ? t('onboarding.getStarted') : t('onboarding.next')}
                {!last && <IonIcon slot="end" icon={arrowForwardOutline} />}
              </IonButton>
            </div>
          </div>
        </div>

        <style>{`
          .onboarding-content {
            --background:
              radial-gradient(
                900px 520px at 12% -8%,
                rgba(var(--ion-color-primary-rgb), 0.22),
                transparent 64%
              ),
              var(--ion-background-color, #060e20);
          }

          .onboarding-shell {
            min-height: 100%;
            display: flex;
            flex-direction: column;
            padding: calc(16px + env(safe-area-inset-top, 0px)) 24px
              calc(24px + env(safe-area-inset-bottom, 0px));
            box-sizing: border-box;
          }

          .onboarding-top {
            display: flex;
            justify-content: flex-end;
            min-height: 40px;
          }

          .onboarding-skip {
            --color: var(--ion-color-medium);
            font-size: 14px;
            margin: 0;
          }

          .onboarding-slide {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 12px;
            padding: 12px 0 28px;
          }

          .onboarding-slide h1 {
            margin: 0;
            font-size: clamp(1.6rem, 5vw, 2.1rem);
            font-weight: 700;
            letter-spacing: -0.02em;
            line-height: 1.15;
          }

          .onboarding-body {
            margin: 0;
            font-size: 1.05rem;
            line-height: 1.5;
            color: var(--ion-color-medium-shade, #a0aec0);
            max-width: 36rem;
          }

          .onboarding-media {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 180px;
            margin-bottom: 8px;
            border-radius: 20px;
            background: rgba(255, 255, 255, 0.04);
          }

          .onboarding-media img {
            width: min(220px, 55vw);
            height: auto;
            object-fit: contain;
          }

          .onboarding-footer {
            display: flex;
            flex-direction: column;
            gap: 18px;
          }

          .onboarding-dots {
            display: flex;
            justify-content: center;
            gap: 8px;
          }

          .onboarding-dot {
            width: 8px;
            height: 8px;
            border-radius: 999px;
            border: none;
            padding: 0;
            background: rgba(255, 255, 255, 0.22);
            cursor: pointer;
          }

          .onboarding-dot.is-active {
            width: 22px;
            background: var(--ion-color-primary);
          }

          .onboarding-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
          }

          .onboarding-actions ion-button {
            margin: 0;
          }
        `}</style>
      </IonContent>
    </IonPage>
  );
};

export default OnboardingPage;
